import React from "react";
import { IconButton } from "@mui/material";
import Interaction from "../../../models/interactions/Interaction";
import { useStoreDispatch, useStoreSelector } from "../../../store/Hooks";
import { navSliceActions } from "../../../store/slices/NavSlice";
import Tooltip from "@material-ui/core/Tooltip";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { stateDeepCopy } from "../../../helpers/DataUpdateHelper";
import { useNavigate } from "react-router-dom";
import {
  buildURLStringFromNodesArray,
  isNodeAlreadyInNavPath,
  returnExistingURLForNavigation,
} from "../../../models/interactions/InteractionRouteParam";
import InteractionNode from "../../../models/interactions/InteractionNode";

interface InteractionPopoutButtonProps {
  interaction: Interaction;
  interactionNode: InteractionNode;
  setID: number | null | undefined;
  parentID: number | null | undefined;
  isInitial: boolean;
  isReferenceSet: boolean;
}

const InteractionPopoutButton = (props: InteractionPopoutButtonProps) => {
  const interactionNodes = useStoreSelector(
    (state) => state.interactionTree.InteractionNodes,
  );
  const navigate = useNavigate();
  const dispatch = useStoreDispatch();

  // recursively examine the interactionNodes tree and add more parents as needed
  // for URL string
  // @TODO - MOVE THIS TO INTERACTIONROUTEPARAMS MODEL
  const getInBetweenInteractionsForDeeplyNestedChild: any = (
    nodeid: number,
    inBetweenInteractionsArray: Array<InteractionNode>,
  ) => {
    const thisIndex = interactionNodes.findIndex((interactionNode) => {
      return interactionNode.id == nodeid;
    });
    const thisNode = interactionNodes[thisIndex];

    // BASE CASE
    //find direct parent of the selected node
    const parentOfThisNodeIndex = interactionNodes.findIndex(
      (interactionNode) => {
        return interactionNode.child_interaction_id == thisNode.parent_interaction_id;
      },
    );

    // BASE CASE
    // return the array if there are no more parents to find
    if (parentOfThisNodeIndex == -1) {
      return inBetweenInteractionsArray;
    }

    // *********fail-safe/catch all*********
    // dont add node to history if we've determined its already there
    // or a functionally equivalant node is already there.
    // (It's considered already existent if we are wanting to add a certain parent
    //  but if that parent is already in the path as EITHER sXXXXX OR pXXXXX,
    //  we dont  want to add it again since its already "handled")
    const parentOfThisNode = interactionNodes[parentOfThisNodeIndex];
    const currentURL = returnExistingURLForNavigation(window.location.href);
    let nodeAlreadyInNavPath = false;
    nodeAlreadyInNavPath = isNodeAlreadyInNavPath(parentOfThisNode,currentURL)

    if(!nodeAlreadyInNavPath){
      inBetweenInteractionsArray.push(parentOfThisNode);
    }

    // recursively examine the tree and add more parents as needed
    return getInBetweenInteractionsForDeeplyNestedChild(
      parentOfThisNode.id,
      inBetweenInteractionsArray,
    );
  };

  // @TODO - MOVE AS MUCH OF THIS AS POSSIBLE TO INTERACTIONROUTEPARAMS MODEL
  async function handlePopoutInteraction() {
    dispatch(
      navSliceActions.addToBreadcrumbs({
        currentCrumb: stateDeepCopy(props.interaction),
      }),
    );

    // What if we are looking at a popped-out pathway, and we want to then
    // pop out an interaction that is a child of a child of a child pathway?
    // For NavHistory, we need to get a list of everything in between
    // the current root interaction and the selected once (not including either)
    // and add to NavHistory
    let inBetweenInteractionNodes: Array<InteractionNode> = [];
    inBetweenInteractionNodes = getInBetweenInteractionsForDeeplyNestedChild(
      props.interactionNode.id,
      inBetweenInteractionNodes,
    );

    // reverse the array to make sure in-between nodes are in the right order
    inBetweenInteractionNodes = inBetweenInteractionNodes.reverse();

    let inBetweenInteractionNodesURLString = "";

    const URLStringFromNodesArray = buildURLStringFromNodesArray(
      inBetweenInteractionNodes,
    );
    if (URLStringFromNodesArray.length > 0) {
      inBetweenInteractionNodesURLString = "_" + URLStringFromNodesArray;
    }
    // if children in different set setID to props.interaction.id
    if (props.setID && props.interaction.id) {
      const currentURL = returnExistingURLForNavigation(window.location.href);
      
      // (Truthfully, as long as the correct parent and node are added here,
      // both PathwayPage and InteractionShell make 2 calls; one for this set,
      // and another for the set of the parent - just to make sure all bases are
      // covered whether or not the interaction is shared)
      navigate(
        currentURL +
          inBetweenInteractionNodesURLString +
          "_s" +
          props.interactionNode.set_interaction_id +
          "p" +
          props.interaction.id +
          "n" +
          props.interactionNode.id,
      );
      
    } else {
      console.error("interaction is missing parent or set");
    }
  }

  return (
    <Tooltip title="Pop out this interaction to fullscreen">
      <IconButton
        data-testid={"interactionPopoutButton" + props.interaction.id}
        onClick={async (event: React.MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          // pop out the selected interaction
          await handlePopoutInteraction();
        }}
      >
        <OpenInFullIcon />
      </IconButton>
    </Tooltip>
  );
};

export default InteractionPopoutButton;
